<template>
  <div class="document-container">
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">Near Match By Location Report Results</div>
          </template>
          <template #actions v-if="results && !loading">
            <ul>
              <li class="p-narrow no-border" v-if="hasModule('PRSID:DWNLD:DOWNLOAD')">
                <div class="list-content">
                  <a @click.prevent="generateReport('XLSX')"
                    @keypress.enter.prevent="generateReport('XLSX')"
                    tabindex="0" role="button"
                  >
                    Generate EXCEL
                  </a>
                </div>
              </li>
              <li class="p-narrow no-border" v-if="hasModule('PRSID:DWNLD:DOWNLOAD')">
                <div class="list-content">
                  <a @click.prevent="generateReport('PDF')"
                    @keypress.enter.prevent="generateReport('PDF')"
                    tabindex="0" role="button"
                  >
                    Generate PDF
                  </a>
                </div>
              </li>
              <li class="p-narrow no-border" style="justify-content: center;">
                <a
                  class="is-primary"
                  v-if="isBuildingLevel || isBatchLevel"
                  @click.prevent="goToPreviousLevel()"
                  @keypress.enter.prevent="goToPreviousLevel()"
                  tabindex="0" role="button"
                >
                  <font-awesome-icon
                    :icon="['far', 'angle-double-left']"
                    style="font-size: 1.8rem;"
                    class="fa-2x"
                    :title="backLinkTitle"
                  />
                </a>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <!-- Summary Header -->
      <es-profile-header v-if="!loading && summaryInfo" class="is-inline">
        <template #profile-header>
          <div class="columns">
            <div class="column">
              <div class="column-content">
                <div class="header-subtitle" v-if="summaryInfo.subtitle">
                  {{summaryInfo.subtitle}}
                </div>

                <div class="header-title">
                  {{summaryInfo.title}}
                </div>

              </div>
            </div>
          </div>
        </template>
        <template #profile-overview>
          <div class="columns">
            <div class="column" v-if="isLocationLevel || isBuildingLevel">
              <div class="column-content">
                <div class="column-title">
                  {{ results.summary.personTypeCount | displayCount }}
                </div>
                <div class="column-subtitle">
                  Person Type <br/> Count
                </div>
              </div>
            </div>
            <div class="column" v-if="isBatchLevel">
            <div class="column-content">
              <div class="column-title">
                {{ results.summary.userCount | displayCount }}
              </div>
              <div class="column-subtitle">
                User <br/> Count
              </div>
            </div>
          </div>
            <div class="column" v-if="isLocationLevel">
              <div class="column-content">
                <div class="column-title">
                  {{ results.summary.districtCount | displayCount }}
                </div>
                <div class="column-subtitle">
                  {{labels.district}}
                  <br/> Count
                </div>
              </div>
            </div>
            <div
                class="column"
                v-if="isBuildingLevel">
              <div class="column-content">
                <div class="column-title">
                  {{ results.summary.schoolCount | displayCount }}
                </div>
                <div class="column-subtitle">
                  {{labels.school}} <br/> Count
                </div>
              </div>
            </div>
            <div class="column">
              <div class="column-content">
                <div class="column-title">
                  {{ results.summary.batchCount | displayCount }}
                </div>
                <div class="column-subtitle">
                  Batch <br/> Count
                </div>
              </div>
            </div>
            <div class="column">
              <div class="column-content">
                <div class="column-title">
                  {{ results.summary.nearMatchCount | displayCount }}
                </div>
                <div class="column-subtitle">
                  Near Match <br/> Count
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-profile-header>
      <!-- Results -->
      <div class="content">
        <es-loader v-if="loading"/>
        <div v-else>
          <es-exception
            v-if="!results || !results.details"
            exceptionMessage="Customize criteria must be entered to view results."
            style="padding: 3rem; margin: 0"
          />
          <es-exception
            v-else-if="!results.details.length"
            exceptionMessage="No results were found."
            style="padding: 3rem; margin: 0"
          />
          <div v-else>
            <div class="table-container">
              <table class="table is-hoverable is-fullwidth">
              <es-sorter
                :apiSorter="results.sorters[0]"
                :columnHeaders="getColumnHeaders"
                @updateSorter="updateSorter"
              />
              <tbody>
                <tr v-for="(item, index) in results.details" :key="index">
                  <td style="width: 55px" v-if="isBatchLevel">
                    <font-awesome-icon v-if="!completedBatch(item.batchInfo)"
                      title="Match Process not yet complete."
                      class="fa-lg has-text-warning"
                      :icon="['fas', 'exclamation-triangle']"
                    />
                  </td>
                  <td class="" v-if="isBatchLevel && item.batchInfo">
                    <div class="is-flex flex-direction--column">
                      <div class="is-flex">
                        <router-link
                          v-if="hasModule('PRSID:MATCH:HISTORY:VIEW')"
                          :to="{ path: `/personId/batch/${item.batchInfo.id}/details` }"
                          class="has-text-weight-bold"
                        >
                          {{ item.batchInfo.id }}
                        </router-link>
                        <span
                            class="padding-left--small has-text-grey max-width-15"
                            v-if="item.batchInfo.fileName">
                          {{ item.batchInfo.fileName }}
                        </span>
                      </div>
                      <div class="is-flex has-text-grey">
                        {{ item.batchInfo.type }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <router-link
                        :to="{
                          name: 'PIDistrictInfoDetails',
                          params: { code: item.districtInfo.code  },
                        }"
                        class="has-text-weight-bold has-text-primary" style="font-size:14px"
                      >
                        {{ item.districtInfo.name }}
                      </router-link>
                      <span class="padding-left--small has-text-grey is-italic">
                        ({{ item.districtInfo.code }})
                      </span>
                    </div>
                    <div v-if="item.schoolInfo">
                      <router-link
                        :to="{
                          name: 'PISchoolInfoDetails',
                          params: {
                            districtCode: item.districtInfo.code,
                            schoolCode: item.schoolInfo.code
                          },
                        }"
                        class="has-text-primary" style="font-size:12px"
                      >
                        {{ item.schoolInfo.name }}
                      </router-link>
                      <span class="padding-left--small has-text-grey is-italic">
                        ({{ item.schoolInfo.code }})
                      </span>
                    </div>
                  </td>
                  <td v-if="item.batchInfo">
                    {{ item.userName }} ({{ item.userEmail}})
                  </td>
                  <td v-if="item.batchInfo">
                    <div class="has-text-weight-bold">{{item.batchInfo.creationDate | date }}</div>
                    <div class="has-text-grey">{{item.batchInfo.creationDate | time}}</div>
                  </td>
                  <td class="has-text-centered" v-if="!item.schoolInfo">
                    {{ item.schoolCount }}
                  </td>
                  <td class="has-text-centered" v-if="!isBatchLevel">
                    <a
                      @click.prevent="goToBatchList(item)"
                      @keypress.enter.prevent="goToBatchList(item)"
                      class="has-text-primary"
                      tabindex="0" role="link"
                    >
                      {{ item.batchCount }}
                    </a>
                  </td>
                  <td class="has-text-centered">
                    {{ item.nearMatchCount }}
                  </td>
                  <td class="action-column has-text-right">
                    <a
                        class="is-primary"
                        @click.prevent="goToNextLevel(item)"
                        @keypress.enter.prevent="goToNextLevel(item)"
                        tabindex="0" role="link">
                      <font-awesome-icon
                        :icon="['far', 'angle-double-right']"
                        style="font-size: 1.8rem;"
                        class="fa-2x"
                        title="View Details"
                      />
                  </a>
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
            <div class="pager-container">
              <es-pager :totalCount="results.totalCount" :pageSize="pageSize"></es-pager>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// components
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsException from '@/components/EsException.vue';
import EsProfileHeader from '@/components/EsProfileHeader.vue';
import EsSorter from '@/components/EsSorter.vue';
import EsPager from '@/components/EsPager.vue';

// mixins
import PermissionManager from '@/mixins/PermissionManager';
import FieldLabels from '@/mixins/FieldLabels';

// icons
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleDoubleRight, faAngleDoubleLeft } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';

// utils
import moment from 'moment';

library.add(
  faAngleDoubleRight,
  faAngleDoubleLeft,
  faExclamationTriangle,
);

export default {
  components: {
    EsHeader, EsLoader, EsException, EsProfileHeader, EsSorter, EsPager,
  },
  mixins: [
    PermissionManager,
    FieldLabels,
  ],
  props: {
    results: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    defaultPersonType: {
      type: String,
      default: '',
    },
    pageSize: {
      type: Number,
      default: 25,
    },
    levelIndex: {
      type: Number,
      default: 0,
    },
  },
  methods: {
    goToNextLevel(item) {
      if (item.batchInfo) {
        this.$router.push({
          path: `/personId/batch/${item.batchInfo.id}/details`,
        });
        return;
      }

      if (item.schoolInfo || item.districtInfo) {
        this.$emit('nextLevel', item);
      }
    },
    goToPreviousLevel() {
      this.$emit('previousLevel');
    },
    goToBatchList(item) {
      this.$emit('goToBatch', item);
    },
    generateReport(type) {
      this.$emit('generateReport', type);
    },
    updateSorter(sorter) {
      this.$emit('updateSorter', { field: sorter.field, order: sorter.order });
    },
    completedBatch(batch) {
      const { status } = batch;
      if (status === 'COMPLETED'
        || status === 'VALIDATION_CANCELED'
        || status === 'REJECTED') {
        return true;
      }
      return false;
    },
  },
  data() {
    return {
      apiSorter: {},
      columnHeaders: [],
      LEVELS: {
        LOCATION: 0,
        SCHOOL: 1,
        BATCH: 2,
      },
    };
  },
  computed: {
    backLinkTitle() {
      let level = '';

      if (this.isBatchLevel) {
        level = this.labels.school;
      } else if (this.isBuildingLevel) {
        level = this.labels.district;
      }

      return `Go back to ${level} Level`;
    },
    isLocationLevel() {
      return this.levelIndex === this.LEVELS.LOCATION;
    },
    isBuildingLevel() {
      return this.levelIndex === this.LEVELS.SCHOOL;
    },
    isBatchLevel() {
      return this.levelIndex === this.LEVELS.BATCH;
    },
    summaryInfo() {
      if (!this.results || !this.results.summary) {
        return null;
      }

      const { summary, details } = this.results;
      const levelParams = JSON.parse(this.$route.query.levelParams)[this.levelIndex];
      const suffix = levelParams.districtCodes || levelParams.schoolKeys ? ' List for' : ' List';
      let title = '';
      let subtitle = '';

      if (this.levelIndex === this.LEVELS.BATCH) {
        if (!this.results.batchId) {
          if (levelParams.districtCodes) {
            const districtCodes = levelParams.districtCodes.split(':');
            if (levelParams.schoolKeys) {
              const schoolKeys = levelParams.schoolKeys.split(':');
              title = `${districtCodes[0]} (${districtCodes[1]}) - ${schoolKeys[0]} (${schoolKeys[1]})`;
            } else {
              title = `${districtCodes[0]} (${districtCodes[1]})`;
            }
          }
          subtitle = 'Batch List for';
        } else {
          title = this.results.batchId;
          subtitle = 'Batch List';
        }
        return { title, subtitle };
      }

      if (summary.schoolInfo) {
        let prefix = '';
        subtitle = `${this.labels.school}${suffix}`;

        if (levelParams.districtCodes) {
          const districtCodes = levelParams.districtCodes.split(':');
          title = `${districtCodes[0]} (${districtCodes[1]})`;
        } else if (summary.schoolInfo.code === 'Multiple') {
          title = summary.schoolInfo.name;
        } else if (summary.schoolInfo.code === 'All') {
          title = `${this.labels.school} List`;
          subtitle = '';
        } else {
          if (details.length < 2) {
            prefix = `${summary.districtInfo.name} (${summary.districtInfo.code}) - `;
          }
          title = `${prefix}${summary.schoolInfo.name} (${summary.schoolInfo.code})`;
        }

        return { title, subtitle };
      }

      if (summary.districtInfo) {
        if (summary.districtInfo.code === 'All') {
          title = `${this.labels.district} List`;
        } else {
          subtitle = `${this.labels.district}${suffix}`;
          if (summary.districtInfo.code === 'Multiple') {
            title = summary.districtInfo.name;
          } else {
            title = `${summary.districtInfo.name} (${summary.districtInfo.code})`;
          }
        }

        return { title, subtitle };
      }

      return null;
    },
    getColumnHeaders() {
      const columnHeaders = [
        {
          header: 'Location Info',
          title: 'Location Info',
          field: this.isLocationLevel ? 'districtInfo' : 'schoolInfo',
          order: null,
          sortable: true,
        },
        {
          header: `${this.labels.school} Count`,
          title: `${this.labels.school} Count`,
          field: 'schoolCount',
          order: null,
          sortable: true,
        },
        {
          header: 'Batch Count',
          title: 'Batch Count',
          field: 'batchCount',
          order: null,
          sortable: true,
        },
        {
          header: 'Near Matches',
          title: 'Near Matches',
          field: 'nearMatchCount',
          order: null,
          sortable: true,
        },
        {
          header: 'Actions',
          title: 'Actions',
          field: null,
        },
      ];

      if (!this.isLocationLevel) {
        columnHeaders.splice(1, 1);
      }

      if (this.isBatchLevel) {
        columnHeaders.splice(1, 1);
        columnHeaders.splice(1, 0, {
          header: 'Creation User',
          title: 'Creation User',
          field: 'user',
          order: null,
          sortable: true,
        });
        columnHeaders.splice(2, 0, {
          header: 'Process Info',
          title: 'Process Info',
          field: 'creationDate',
          order: null,
          sortable: true,
        });
        columnHeaders.unshift({
          header: 'Batch Info',
          title: 'Batch Info',
          field: 'batchInfo',
          order: null,
          sortable: true,
        });
        columnHeaders.unshift({
          header: '',
        });
      }

      return columnHeaders;
    },
    labels() {
      return {
        district: this.getFieldLabel(`PER:${this.defaultPersonType}:ASSIGN:DISTRICT_CODE`),
        school: this.getFieldLabel(`PER:${this.defaultPersonType}:ASSIGN:SCHOOL_CODE`),
      };
    },
  },
  filters: {
    date(date) {
      return moment(date).format('MM/DD/YYYY');
    },
    time(date) {
      return moment(date).format('hh:mm A');
    },
    displayCount(count) {
      return count || 'N/A';
    },
  },
};
</script>

<style lang="sass" scoped>
  .padding-left--small
    padding-left: 0.25rem
  .max-width-15
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    max-width: 20ch
  .table-container
    overflow-x: auto
  .pager-container
    border-bottom: 1px solid #dee2e6
    border-width: 1px 0 0
    padding: 5.6px 12px
    background-color: #f9f9f9
</style>
